import { Component, inject, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { SharedTableComponent } from '@tamilarasitech/shared-ui-components';
import { TableService } from 'src/app/components/shared/table.service';
import { FirebaseService } from '../../../app/services/firebase.service';

// Firestore imports for the advanced "deleteField" update
import {
  Firestore,
  collection,
  doc,
  getDocs,
  query,
  limit,
  startAfter,
  updateDoc,
  deleteField
} from '@angular/fire/firestore';

@Component({
  selector: 'app-category',
  standalone: true,
  imports: [CommonModule, SharedTableComponent],
  template: `
    <tt-shared-table
      #sharedTable
      [name]="'category'"
      (loadPage)="onLoadPage($event, sharedTable)"
      (getTotal)="tableService.onGetTotal($event, sharedTable)"
      (add)="onAdd($event)"
      (update)="onUpdate($event)"
      (delete)="tableService.onDelete($event)"
      (deleteBatch)="tableService.onDeleteBatch($event)"
      (deleteAllEmit)="tableService.onDeleteAll()"
      (uploadFile)="tableService.onUploadFile($event)"
      (importData)="onImportData($event)"
    ></tt-shared-table>
  `
})
export class CategoryComponent {
  tableService = inject(TableService);
  private firebaseService = inject(FirebaseService);
  cdr = inject(ChangeDetectorRef);

  // We'll still need direct Firestore references here for advanced partial updates
  private firestore: Firestore = inject(Firestore);

  private currentPath = '';

  /**
   * 1. onLoadPage
   *    - calls FirebaseService.loadPage() to get {data, lastDoc, hasMore}
   *    - rebuilds "extra" fields into a .document JSON string for each item
   */
  async onLoadPage(
    event: { path: string; pageSize: number; lastDoc?: any },
    tableRef: any
  ): Promise<void> {
    console.log('🔄 [Category] onLoadPage() called:', event);
    tableRef.loading = true;

    try {
      this.currentPath = event.path;
      this.firebaseService.setPath(event.path); // keep path consistent
      const { data, lastDoc, hasMore } = await this.firebaseService.loadPage(
        event.path,
        event.pageSize,
        event.lastDoc
      );

      // Step 2: rebuild "document" from non-protected columns
      const protectedFields = ['name', 'subCategory', 'language', 'id'];

      const finalData = data.map(raw => {
        const item = { ...raw };
        const extraFields: Record<string, any> = {};

        // Move any non-protected fields into a "document" JSON for the editor
        Object.keys(item).forEach(key => {
          if (!protectedFields.includes(key)) {
            extraFields[key] = item[key];
          }
        });

        if (Object.keys(extraFields).length > 0) {
          item.document = JSON.stringify(extraFields, null, 2);
        }
        return item;
      });

      // Step 3: update the table
      tableRef.data = finalData;
      tableRef.hasMore = hasMore;
      tableRef.lastDoc = lastDoc;

      if (tableRef.totalRecords > 0) {
        tableRef.updateDisplayIndices();
      }

      console.log('✅ [Category] Table data updated:', {
        dataLength: finalData.length,
        hasMore,
        totalRecords: tableRef.totalRecords
      });
    } catch (error) {
      console.error('❌ [Category] Error loading data:', error);
      tableRef.error = error instanceof Error ? error.message : 'Failed to load data';
    } finally {
      tableRef.loading = false;
    }
  }

  /**
   * 2. onAdd
   *    - flattens the 'document' JSON into top-level fields
   *    - calls firebaseService.add() to perform the actual insert
   */
  async onAdd(event: { path: string; item: any }): Promise<void> {
    try {
      const { path, item } = event;
      const protectedFields = ['name', 'subCategory', 'language'];

      // Flatten "document" into top-level
      const transformed = { ...item };
      if (transformed.document) {
        try {
          const docJson = typeof transformed.document === 'string'
            ? JSON.parse(transformed.document)
            : transformed.document;

          if (!docJson || typeof docJson !== 'object' || Array.isArray(docJson)) {
            throw new Error('Document must be a valid JSON object');
          }

          // Extract non-protected fields
          Object.keys(docJson).forEach(key => {
            if (!protectedFields.includes(key)) {
              transformed[key] = docJson[key];
            }
          });
        } catch (e) {
          console.error('Invalid JSON in "document" field:', e);
          throw new Error('Invalid JSON in document field. Must be a valid object.');
        }
        delete transformed.document;
      }

      await this.firebaseService.add(path, transformed);
      console.log('✅ Document added successfully.');
    } catch (error) {
      console.error('❌ Error adding document:', error);
      throw error;
    }
  }

  /**
   * 3. onUpdate
   *    - retrieves the current doc to see which keys have been removed
   *    - merges new "document" JSON
   *    - deletes missing/removed keys via deleteField()
   *    - also removes protected fields from the local document JSON so it doesn't show up in the UI
   */
  async onUpdate(event: { id: string; data: any }): Promise<void> {
    const { id, data } = event;
    const protectedFields = ['name', 'subCategory', 'language', 'id'];

    try {
      // Step 1: parse the new "document" JSON
      let extraFields: Record<string, any> = {};
      if (data.document) {
        const docJson = typeof data.document === 'string'
          ? JSON.parse(data.document)
          : data.document;

        if (!docJson || typeof docJson !== 'object' || Array.isArray(docJson)) {
          throw new Error('Document must be a valid JSON object');
        }

        // Extract only non-protected fields from docJson
        Object.keys(docJson).forEach(key => {
          if (!protectedFields.includes(key)) {
            extraFields[key] = docJson[key];
          }
        });

        // Immediately remove protected fields from the local docJson
        protectedFields.forEach(field => {
          delete docJson[field];
        });

        // Update data.document so the UI won't still show protected fields
        data.document = JSON.stringify(docJson, null, 2);
      }

      // Step 2: fetch existing doc to see what fields must be deleted
      const colRef = collection(this.firestore, this.currentPath);
      const snapshot = await getDocs(colRef);
      const currentDocSnap = snapshot.docs.find(d => d.id === id);
      if (!currentDocSnap) {
        throw new Error(`Could not find document with ID '${id}'`);
      }

      const currentData = currentDocSnap.data() || {};

      // Step 3: figure out which keys were removed
      const removedKeys = Object.keys(currentData).filter(key => {
        return !protectedFields.includes(key) && !(key in extraFields);
      });

      // Step 4: build final payload
      const updatePayload: Record<string, any> = { ...extraFields };
      removedKeys.forEach(key => {
        updatePayload[key] = deleteField();
      });

      // Preserve protected fields if they come from the form
      protectedFields.forEach(field => {
        if (data[field] !== undefined && !updatePayload.hasOwnProperty(field)) {
          updatePayload[field] = data[field];
        }
      });

      // Step 5: actually update Firestore
      const docRef = doc(this.firestore, this.currentPath, id);
      await updateDoc(docRef, updatePayload);

      console.log('✅ Document updated successfully:', id);
    } catch (error) {
      console.error('❌ Error updating document:', error);
      throw error;
    }
  }

  /**
   * 4. onImportData
   *    - the table expects data in "document" so it can be flattened by the service
   */
  async onImportData(event: { items: any[] }): Promise<void> {
    const wrappedItems = event.items.map(item => ({ document: item }));
    await this.firebaseService.importData(wrappedItems);
    console.log('✅ Import completed');
  }
}